import React, { useMemo } from "react";
import { AvatarRing } from "../../../common/comms/AvatarRing";
import { RetryBanner } from "../../../common/comms/RetryBanner";
import { SkeletonRows } from "../../../common/comms/SkeletonRows";
import { StatusBadge } from "../../../common/comms/StatusBadge";
import { formatLocalDate } from "../../../utils/formatLocalDate";
import { latestPerUser } from "./latestBookings";
import { canOpenStatusEditor } from "./bookingAuthority";
import { normaliseStatus, STATUS, statusTone } from "./bookingStatus";
import { hasClientFeedback, ratingOf } from "./clientActivity";
import { callTypeLabelOf, formatShortDate, formatSlotRange } from "./slotGrid";

/** Stable avatar colour per client — see ChatDockUserList for the rationale. */
const colorIndexFor = (id) => (id ? String(id).charCodeAt(0) % 8 : 0);

/**
 * One client on the selected day: their latest call, and the ways into it.
 *
 * A click anywhere on the row opens the activity — the name alone was too small
 * a target. Buttons cannot nest, though, and the row holds others (Set status,
 * the rating chip), so the row's own handler is a mouse convenience only: it
 * ignores clicks that land on an inner button, which do their own thing. The
 * name stays a real button, as the keyboard and screen-reader way in.
 *
 * Set Status is hidden rather than disabled when the viewer cannot use it: an
 * action nobody in this role can ever take is noise on every row of the day.
 * The one case it *is* shown without an available option is a coach who has had
 * their one change — the lock is worth surfacing, because "ask your manager" is
 * the answer to the question they are about to ask.
 *
 * `callType` ("First call", "Regular call", …) is left out rather than shown
 * empty when it can't be named — a call type deleted since, or the list not in.
 *
 * An off-shift call (booked outside the coach's shift hours) is tagged
 * "Off-shift". The API marks one with a null `coach_shift_id`; a record that
 * doesn't carry the field at all isn't tagged.
 *
 * A rated call carries its rating as "★ 4.0", under the same rule as the
 * activity drawer: only while the latest status is Completed
 * (`hasClientFeedback`). The chip is a way in — it opens the drawer on this
 * call with its feedback already unfolded, so the rating is never a number
 * without the words behind it. A Completed call with no rating says
 * "Not rated" instead, as the activity drawer does; other statuses say nothing.
 */
const DayBookingRow = ({ booking, callType, dayKey, onOpenClient, onSetStatus, canSetStatus }) => {
  const name = booking.user_name || "Unknown User";
  // The day goes with the client: the activity panel opens on this day's calls.
  const openActivity = () => onOpenClient({ id: booking.user_id, name, dayKey });
  const rating = hasClientFeedback(booking) ? ratingOf(booking) : null;
  const notRated =
    rating == null && normaliseStatus(booking.booking_status) === STATUS.COMPLETED;
  const offShift = booking.coach_shift_id === null;
  const openFeedback = () =>
    onOpenClient({ id: booking.user_id, name, dayKey, focusBookingId: booking.id });

  return (
    // Not focusable or given a role on purpose: the name button below is the
    // accessible version of this click, and a second tab stop would duplicate it.
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-noninteractive-element-interactions
    <li
      onClick={(event) => {
        if (event.target.closest("button, a")) return;
        openActivity();
      }}
      // `relative` is load-bearing, not decoration: Tailwind's `.sr-only` is
      // absolutely positioned, and without a positioned ancestor it escapes
      // the app shell's clipping and adds a blank band under the page.
      className="relative flex cursor-pointer flex-wrap items-center gap-4 border-b border-gray-100 px-6 py-4 transition-colors last:border-b-0 hover:bg-light-orange/30"
    >
      <p className="min-w-[132px] font-semibold text-orange-primary">
        {formatSlotRange(booking)}
      </p>
      <AvatarRing
        name={booking.user_name}
        colorIndex={colorIndexFor(booking.user_id || booking.user_name)}
        size="sm"
      />
      <button
        type="button"
        onClick={openActivity}
        aria-label={`${name}, ${formatSlotRange(booking)}, ${
          callType ? `${callType}, ` : ""
        }${offShift ? "off-shift, " : ""}${booking.booking_status || "Unknown"} — view booking activity`}
        className="min-w-[140px] text-left font-semibold text-bc-text transition-colors hover:text-orange-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-primary focus-visible:ring-offset-1"
      >
        {name}
      </button>
      {/* The latest status change's note — bookings no longer carry a client-stated reason. */}
      <p className="min-w-0 flex-1 truncate text-bc-soft">
        {booking.status_reason || ""}
      </p>
      {offShift ? <StatusBadge tone="violet">Off-shift</StatusBadge> : null}
      {callType ? <StatusBadge tone="info">{callType}</StatusBadge> : null}
      {rating != null ? (
        <button
          type="button"
          onClick={openFeedback}
          aria-label={`Rated ${Number(rating.toFixed(1))} out of 5 — view ${name}'s feedback`}
          className="inline-flex items-center gap-1 rounded-full bg-light-orange px-2 py-0.5 text-[11px] font-semibold text-orange-primary transition-colors hover:bg-orange-primary hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-primary focus-visible:ring-offset-1"
        >
          <span aria-hidden="true">★</span>
          {rating.toFixed(1)}
        </button>
      ) : notRated ? (
        <span className="inline-flex items-center rounded-full border border-dashed border-gray-300 px-2 py-0.5 text-[11px] font-semibold text-bc-faint">
          Not rated
        </span>
      ) : null}
      <StatusBadge tone={statusTone(booking.booking_status)}>
        {booking.booking_status || "Unknown"}
      </StatusBadge>
      {canSetStatus ? (
        <button
          type="button"
          onClick={() => onSetStatus(booking)}
          aria-label={`Set status for ${name}, ${formatSlotRange(booking)}`}
          className="min-h-[36px] whitespace-nowrap rounded-lg border border-gray-200 bg-white px-3 text-xs font-semibold text-bc-text-2 transition-colors hover:bg-light-gray/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-primary focus-visible:ring-offset-1"
        >
          Set status
        </button>
      ) : null}
    </li>
  );
};

/**
 * The bookings dashboard — the calls clients have actually booked on the
 * selected day.
 *
 * A controlled panel: the day, the data and the month all come from
 * `BookingsWorkspace`, which owns the calendar above it. This renders the day
 * and nothing else.
 *
 * **One row per client, not per call.** `useBookings` has already collapsed the
 * API's append-only trail to one row per call, which is a correctness rule;
 * `latestPerUser` collapses once more, to the client's last call of the day,
 * which is a product one. A client who booked, cancelled, and rebooked used to
 * take three rows and read as three different people's worth of calendar. The
 * folded calls are not lost — the row opens the activity panel, which shows
 * that client's whole trail for the month, including the cancellations this
 * list never showed in the first place.
 *
 * The collapse is **silent**: a "+N more today" pill used to mark a row standing
 * for several calls, and it was removed by request. `latestPerUser` still
 * returns `callCount`, so restoring it is a one-line change. Worth knowing what
 * went with it — a row is now indistinguishable whether it stands for one call
 * or four, and the Set status button on it acts on the client's **latest** call
 * only. The earlier ones are reachable from the activity panel, one row each,
 * but nothing on this list says so.
 *
 * @param {Object} props
 * @param {Date|null} props.selectedDate
 * @param {Record<string, Array<Object>>} props.bookingsByDate - Already
 *   narrowed to the employee record being viewed.
 * @param {boolean} props.hasMonthData - Lets an empty day be told apart from
 *   one that simply hasn't loaded.
 * @param {Error|null} [props.error]
 * @param {() => void} [props.refresh]
 * @param {boolean} [props.viewingCoach=false] - Whose bookings failed to load,
 *   for the retry banner's wording.
 * @param {(client: { id: string, name: string, dayKey: string, focusBookingId?: string }) => void} props.onOpenClient -
 *   Opens the activity panel on that client's calls on the selected day (`dayKey`, `YYYY-MM-DD`).
 *   The rating chip also names its call (`focusBookingId`), whose feedback opens unfolded.
 * @param {(booking: Object) => void} [props.onSetStatus] - Opens the status
 *   editor for one call. Omit it and the action never renders.
 * @param {{ isExecutive: boolean, ownsSlot: boolean, managesSlot: boolean }} [props.statusAuthority]
 * @param {Map<string, { id: string, title: string }>} [props.statusIndex]
 * @param {Map<string, string>} [props.callTypeLabels] - Call-type label by
 *   `shift_call_type_id` (`callTypeLabelsById`), for the chip on each row. A
 *   `call_type` the record carries itself takes precedence.
 * @param {() => void} [props.onBookOffShift] - Opens the off-shift booking
 *   modal. Omit it and the button never renders.
 */
export const BookingsTab = ({
  selectedDate,
  bookingsByDate,
  hasMonthData,
  error,
  refresh,
  viewingCoach = false,
  onOpenClient,
  onSetStatus,
  statusAuthority,
  statusIndex,
  callTypeLabels,
  onBookOffShift,
}) => {
  const dayKey = selectedDate ? formatLocalDate(selectedDate) : null;
  const dayBookings = useMemo(
    () => (dayKey ? latestPerUser(bookingsByDate?.[dayKey] || []) : []),
    [dayKey, bookingsByDate]
  );

  return (
    <div className="space-y-5">
      {error ? (
        <RetryBanner
          message={
            error.message ||
            (viewingCoach
              ? "Something went wrong loading these bookings."
              : "Something went wrong loading your bookings.")
          }
          onRetry={refresh}
        />
      ) : null}

      {selectedDate ? (
        <section
          aria-labelledby="bookings-day-heading"
          className="rounded-xl border border-gray-200 bg-white"
        >
          <div className="flex flex-wrap items-baseline gap-2 px-6 pt-5">
            <h2 id="bookings-day-heading" className="text-base font-semibold text-bc-text">
              {`Bookings on ${formatShortDate(selectedDate)}`}
            </h2>
            {/*
              Says both collapses at once. The trail collapse is what makes the
              status accurate — without it a rescheduled call sits here twice,
              disagreeing with itself — and the per-client one is what makes
              the count of rows a count of people.
            */}
            <p className="text-xs text-bc-soft">
              latest call per client · open a row for their full activity
            </p>
            {onBookOffShift ? (
              <button
                type="button"
                onClick={onBookOffShift}
                className="ml-auto min-h-[36px] self-center rounded-lg bg-orange-primary px-3 text-sm font-semibold text-white transition-opacity hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-primary focus-visible:ring-offset-1"
              >
                + Book off-shift call
              </button>
            ) : null}
          </div>

          <div className="mt-4 pb-2">
            {!hasMonthData ? (
              <div className="px-6 pb-4">
                <SkeletonRows count={3} />
              </div>
            ) : dayBookings.length === 0 ? (
              <p className="px-6 py-8 text-center text-sm text-bc-soft">
                No calls booked on this day.
              </p>
            ) : (
              <ul>
                {dayBookings.map((booking) => (
                  <DayBookingRow
                    key={booking.id}
                    booking={booking}
                    callType={callTypeLabelOf(booking, callTypeLabels)}
                    dayKey={dayKey}
                    onOpenClient={onOpenClient}
                    onSetStatus={onSetStatus}
                    canSetStatus={
                      Boolean(onSetStatus) &&
                      canOpenStatusEditor({
                        authority: statusAuthority,
                        booking,
                        statusIndex,
                      })
                    }
                  />
                ))}
              </ul>
            )}
          </div>
        </section>
      ) : (
        <section className="rounded-xl border border-gray-200 bg-white px-6 py-8">
          <p className="text-center text-sm text-bc-soft">
            Pick a day above to see the calls booked on it.
          </p>
        </section>
      )}
    </div>
  );
};
